import {
  AccidentIssueWithCache,
  AccidentIssueWithCacheSearch,
  SearchAccidentIssueWithCacheResponseAccidentIssueWithCacheConnectionArgs,
  SortDirectionEnum,
} from '@/models/gen/graphql';
import { ConnectionDetails, convertConnectionToDetails, mergeConnectionDetails as merge } from '@/utils/custom';
import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import { QueryInputType, getProperty, queryInput } from '@/utils';
import { createAccidentIssueDocumentDownloadUrl, recreateAccidentIssueDocumentDownloadUrl } from './createAccidentIssueDocumentDownloadURL';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';
import {
  refetchAccidentIssueDocument,
  searchAccidentIssueDocument,
} from '@/features/AccidentHistory/api/services/searchAccidentIssueDocument';

import { AccidentIssueWithDocuments } from '@/features/AccidentHistory/components/AccidentHistoryForm/types';
import { SearchAccidentIssue } from '@/features/AccidentHistory/api/queries/SearchAccidentIssue';
import { TypedDocumentNode } from '@apollo/client';
import graphApi from '@/api';

// Service Function Signature
type ServiceMethodPayload = ({
  startDatetime?: string;
  endDatetime?: string;
  airportCode?: string[];
  tripServicerIataAirlineCodes?: string[];
} & { [key in keyof AccidentIssueWithCacheSearch]?: any })[];
interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<AccidentIssueWithCache>>;
}

interface ServiceMethodWithDocuments {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<AccidentIssueWithDocuments>>;
}

// SearchAccidentIssue Service
export const { searchAccidentIssue, refetchAccidentIssue, useAccidentIssue } = ((): {
  searchAccidentIssue: ServiceMethod;
  refetchAccidentIssue: ServiceMethod;
  useAccidentIssue: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch, refetch] = graphApi(SearchAccidentIssue).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<AccidentIssueWithCache>> => {
        const query: AccidentIssueWithCacheSearch[] = payload?.map((item) => ({
          tripId: queryInput.orNull(item?.id),
          createdAt:
            item?.startDatetime && item?.endDatetime
              ? queryInput.date([item?.startDatetime, item.endDatetime], false, SortDirectionEnum.Desc)
              : item?.id // If we are searching by trip id this is a history table, and we can get all the dispatch issues and sort by datetime
                ? queryInput([], QueryInputType.ISNOTNULL, SortDirectionEnum.Desc)
                : null,
          stationText: queryInput.orNull(item?.airportCode),
          tripServicerIataAirlineCode: queryInput.orNull(item?.tripServicerIataAirlineCodes),
          anyoneInjured: queryInput(item?.anyoneInjured),
        }));
        const args: SearchAccidentIssueWithCacheResponseAccidentIssueWithCacheConnectionArgs = {
          input: {
            first: options?.pageSize || null,
            after: options?.pageSize > 0 ? (options?.pageSize * options?.page).toString() : null,
            query,
          },
        };
        const opts = { merge: options?.merge ? 'searchAccidentIssue.accidentIssueTableConnection' : undefined };
        const res = await fn(args, opts);
        return convertConnectionToDetails(getProperty('searchAccidentIssue.accidentIssueTableConnection', res));
      }
  );
  const useService = createGraphApiHook(fetch, { refetch, merge });
  return {
    searchAccidentIssue: fetch,
    refetchAccidentIssue: refetch,
    useAccidentIssue: useService,
  };
})();

export const { searchAccidentIssueWithDocuments, refetchAccidentIssueWithDocuments } = ((): {
  searchAccidentIssueWithDocuments: ServiceMethodWithDocuments;
  refetchAccidentIssueWithDocuments: ServiceMethodWithDocuments;
} => {
  const fetch = async (
    payload?: ServiceMethodPayload,
    options?: ConnectionOptions
  ): Promise<ConnectionDetails<AccidentIssueWithDocuments>> => {
    const accidentIssues = await searchAccidentIssue(payload, options);
    const res: ConnectionDetails<AccidentIssueWithDocuments> = {
      ...accidentIssues,
      rows: accidentIssues.rows.map((row) => ({
        ...row,
        documents: [],
      })),
    };
    const allIds = res.rows.map((row) => row.id);
    if (allIds?.length > 0) {
      const documents = await searchAccidentIssueDocument([{ issueId: queryInput(allIds) }]);
      try {
        const downloadURLS = await createAccidentIssueDocumentDownloadUrl({ issueId: allIds });
        documents.rows.forEach((document) => {
          const issueIndex = res.rows.findIndex((row) => row.id === document.issueId);
          const downloadUrlIndex = downloadURLS.urls.findIndex((url) => url.documentId === document.id);
          if (issueIndex !== -1) {
            res.rows[issueIndex].documents.push({ ...document, fileLocation: downloadURLS.urls[downloadUrlIndex]?.url });
          }
        });
      } catch (err) {
        console.error('Error creating download URLs:', err);
      }
    }
    return { ...res };
  };

  const refetch = async (
    payload?: ServiceMethodPayload,
    options?: ConnectionOptions
  ): Promise<ConnectionDetails<AccidentIssueWithDocuments>> => {
    const accidentIssues = await refetchAccidentIssue(payload, options);
    const res: ConnectionDetails<AccidentIssueWithDocuments> = {
      ...accidentIssues,
      rows: accidentIssues.rows.map((row) => ({
        ...row,
        documents: [],
      })),
    };
    const allIds = res.rows.map((row) => row.id);
    if (allIds?.length > 0) {
      const documents = await refetchAccidentIssueDocument([{ issueId: queryInput(allIds) }]);
      try {
        const downloadURLS = await recreateAccidentIssueDocumentDownloadUrl({ issueId: allIds });
        documents.rows.forEach((document) => {
          const issueIndex = res.rows.findIndex((row) => row.id === document.issueId);
          const downloadUrlIndex = downloadURLS.urls.findIndex((url) => url.documentId === document.id);
          if (issueIndex !== -1) {
            res.rows[issueIndex].documents.push({ ...document, fileLocation: downloadURLS.urls[downloadUrlIndex]?.url });
          }
        });
      } catch (err) {
        console.error('Error creating download URLs:', err);
      }
    }
    return { ...res };
  };
  return {
    searchAccidentIssueWithDocuments: fetch,
    refetchAccidentIssueWithDocuments: refetch,
  };
})();
